import S from 's-js';
import { Client } from 'lib/Ajax';

export interface ASPNetMVCModelState {
    ModelState: {
        [ field : string ] : string[];
    }
}

export interface Errors {
    [field : string] : Error;
}

export interface Error extends ReturnType<typeof Error> { }

export type ValidationWatcher = (v? : Errors) => Errors;

export function ValidationWatcher<T, U>(client : Client<T, U>) : ValidationWatcher {
    var validation = S.data<Errors>({});

    S.on(client.LastResponse, () => {
        var res = client.LastResponse();
        validation(res !== null && res.Status === 400 ? parseModelStateErrors(res.Data as any as ASPNetMVCModelState) : {});
    }, null, true);

    return validation;
}

export function parseModelStateErrors(data : ASPNetMVCModelState) {
    var parsed = {} as Errors;

    for (var field in data.ModelState) {
        var errors = data.ModelState[field];
        var dot = field.indexOf('.');
        if (dot !== -1)
            field = field.substr(dot + 1);
        parsed[field] = Error(field, errors);
    }

    return parsed;
}

function Error(field : string, errors : string[]) {
    const errorTexts = errors.map(err => translateText(field, err));
    return {
        Field: field,
        RawErrors: errors,
        ErrorTexts: errorTexts,
        ErrorText: errorTexts[0] || ""
    };
}

export function mergeErrors(...errors : Errors[]) {
    const merged = {} as Errors;
    for (let errs of errors) {
        for (let key in errs) {
            merged[key] = merged[key] ?? {
                Field: key,
                RawErrors: [],
                ErrorTexts: [],
                ErrorText: ""
            };
            merged[key].RawErrors = merged[key].RawErrors.concat(errs[key].RawErrors);
            merged[key].ErrorTexts = merged[key].ErrorTexts.concat(errs[key].ErrorTexts);
            merged[key].ErrorText = merged[key].ErrorText || errs[key].ErrorText;
        }
    }
    return merged;
}

/**
 * Filter an errors collection by error key, returning a new errors collection.
 * @param errors The errors collection to filter.
 * @param keyFilterFn A function to determine which error keys to keep. Avoid modifying the provided errors
 * collection from this keyFilter function, as that may cause unexpected behavior or JS exceptions.
 */
export function filterErrors(errors : Errors, keyFilterFn : (key : string) => boolean) {
    const out : Errors = {};
    for (let key in errors) {
        if (keyFilterFn(key)) {
            out[key] = errors[key];
        }
    }
    return out;
}

/**
 * Produce a new errors collection based on another errors collection but with keys transformed.
 * @param errors The errors collection to base the new collection off of.
 * @param keyMapper A function to perform the key transformations. Avoid modifying the provided errors
 * collection from this keyMapper function, as that may cause unexpected behavior or JS exceptions.
 */
export function mapErrorKeys(errors : Errors, keyMapper : (key : string) => string) {
    const out : Errors = {};
    for (let key in errors) {
        out[keyMapper(key)] = errors[key];
    }
    return out;
}

const CAMEL_CASE_BOUNDARY = /([a-z])([A-Z])/g;
const REQUIRED = /required/i;
const INTEGER = /integer/i;
const DOUBLE = /double/i;
const MAXIMUM_LENGTH_OF = /maximum length of (\d+)/;
const MINIMUM_LENGTH_OF = /minimum length of (\d+)/;

function translateText(field : string, error : string) {
    var dot = field.indexOf('.');
    // for complex fields, return original error unchanged
    if (dot !== -1) return error;

    // convert from camel case to capitalized text with spaces
    field = field.replace(CAMEL_CASE_BOUNDARY, "$1 $2");

    if (REQUIRED.test(error)) {
        return field + " is required.";
    } else if (INTEGER.test(error)) {
        return field + " must be an integer.";
    } else if (DOUBLE.test(error)) {
        return field + " must be a number.";
    }

    var maxLengthMatch = MAXIMUM_LENGTH_OF.exec(error);
    var minLengthMatch = MINIMUM_LENGTH_OF.exec(error);

    if (minLengthMatch && maxLengthMatch)
        return field + " must be between " + minLengthMatch[1]
            + " and " + maxLengthMatch[1] + " characters.";

    if (maxLengthMatch)
        return field + " cannot be more than " + maxLengthMatch[1] + " characters.";

    if (minLengthMatch)
        return field + " must be at least " + minLengthMatch[1] + " characters.";

    return error;
}


// WEBPACK FOOTER //
// ./lib/validationWatcher.ts